/**
 * `<ts-carousel-arrows>` — scoped navigation controller for the shared
 * `ts-horiz-carousel` layout.
 *
 * The Horizon `slideshow-component` derives its prev/next disabled state from a
 * "visible slide count" page heuristic. On few-card "peek" carousels that
 * wrongly disables BOTH arrows: when ~all cards clear the 70% visibility
 * threshold the component thinks it is already at the end, so `next` is
 * disabled even at the start. That makes the carousel un-navigable.
 *
 * This controller overrides the prev/next `disabled` state — and the arrows'
 * auto-hide + right-align — from the scroller's real dimensions: the arrows
 * stay operable whenever there is real overflow and hide only when the track
 * fully fits. The arrows keep their `on:click` bindings; the component still
 * performs the scroll, we only correct which arrows are operable. We re-assert
 * after the component's own `slideshow:select` (which fires after it sets its
 * heuristic disabled state), so ours is the value that sticks.
 *
 * Extends HTMLElement, NOT `Component` from `@theme/component`: a Component
 * ancestor claims the wrapped `slideshow-component`'s descendant `ref`
 * attributes (`scroller`, `previous`, `next`) and would break the slideshow.
 * We only read our own DOM via querySelector.
 */
const SCROLL_TOLERANCE = 2; // px — absorbs sub-pixel rounding

class TsCarouselArrows extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;
  /** @type {HTMLElement | null} */
  #arrows = null;
  /** @type {HTMLButtonElement | null} */
  #prev = null;
  /** @type {HTMLButtonElement | null} */
  #next = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {MutationObserver | null} */
  #mutationObserver = null;
  #fits = false;

  connectedCallback() {
    this.#scroller = this.querySelector('slideshow-slides');
    this.#arrows = this.querySelector('.ts-horiz-carousel__arrows');
    this.#prev = this.querySelector('.ts-horiz-carousel__arrow--prev');
    this.#next = this.querySelector('.ts-horiz-carousel__arrow--next');

    if (!this.#scroller || !this.#arrows) return;

    // Scroll only changes the disabled state (cheap). Whether the track fits
    // only changes on resize, so measure there and once on load.
    this.#scroller.addEventListener('scroll', this.#applyDisabled, { passive: true });
    this.addEventListener('slideshow:select', this.#applyDisabled);

    this.#resizeObserver = new ResizeObserver(this.#measure);
    this.#resizeObserver.observe(this.#scroller);

    // Re-measure when slides are added/removed — some carousels (Nosto,
    // recently viewed) populate their cards client-side after load, which the
    // ResizeObserver alone may not catch.
    this.#mutationObserver = new MutationObserver(this.#measure);
    this.#mutationObserver.observe(this.#scroller, { childList: true });

    requestAnimationFrame(this.#measure);
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#applyDisabled);
    this.removeEventListener('slideshow:select', this.#applyDisabled);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
  }

  /**
   * Recompute whether the cards fit, measured from the cards themselves.
   * scrollWidth is unreliable once justify-content: flex-end is applied (the
   * start-side overflow isn't reflected), which left the arrows stuck hidden
   * when the viewport shrank back down — so sum the slides + gaps instead.
   */
  #measure = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const style = getComputedStyle(scroller);
    const gap = parseFloat(style.columnGap) || 0;
    const inlinePadding = (parseFloat(style.paddingInlineStart) || 0) + (parseFloat(style.paddingInlineEnd) || 0);

    let content = inlinePadding;
    let first = true;
    for (const slide of scroller.children) {
      content += slide.getBoundingClientRect().width;
      if (!first) content += gap;
      first = false;
    }

    this.#fits = content <= scroller.clientWidth + SCROLL_TOLERANCE;
    this.toggleAttribute('data-fits', this.#fits);
    this.#applyDisabled();
  };

  /** Disabled state from the actual scroll position (both arrows off when fits). */
  #applyDisabled = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    if (this.#fits) {
      if (this.#prev) this.#prev.disabled = true;
      if (this.#next) this.#next.disabled = true;
      return;
    }

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    // Math.abs handles RTL, where scrollLeft is negative.
    const position = Math.abs(scroller.scrollLeft);
    if (this.#prev) this.#prev.disabled = position <= SCROLL_TOLERANCE;
    if (this.#next) this.#next.disabled = position >= maxScroll - SCROLL_TOLERANCE;
  };
}

if (!customElements.get('ts-carousel-arrows')) {
  customElements.define('ts-carousel-arrows', TsCarouselArrows);
}
